import { DeliverableType, ValidationLane, WorkspaceCode, WorkspaceSectionCode } from '@atlas/types'
import { z } from 'zod'

// ─── Per-section options schemas ────────────────────────────────────────────
// Each section owns the shape of its `options`; a section without declared
// options accepts only the empty object, so a typo never persists silently.

const emptySectionOptionsSchema = z.object({}).strict()

const messagesSectionOptionsSchema = z
	.object({
		editable: z.boolean().optional(),
	})
	.strict()

/** External review offers the Workfront hand-off on the deliverables card; internal review does not. */
const finalDeliverablesSectionOptionsSchema = z
	.object({
		workfrontUpload: z.boolean().optional(),
	})
	.strict()

/** Reference cards the Atlas ToBe design shows folded to their header until clicked. */
const collapsibleSectionOptionsSchema = z
	.object({
		collapsed: z.boolean().optional(),
	})
	.strict()

/**
 * One configurable delivery slot: which deliverable classification fills it,
 * whether the workflow's `delivery_slots_filled` guard treats it as
 * mandatory, and the human wording the card shows (the design's
 * `MANDATORY · JPG` treatment travels in `requirementLabel`).
 */
const deliverySlotSchema = z
	.object({
		key: z
			.string()
			.min(1)
			.max(32)
			.regex(/^[a-z0-9_]+$/),
		label: z.string().min(1).max(64),
		sub: z.string().max(120).optional(),
		required: z.boolean(),
		requirementLabel: z.string().max(32).optional(),
		/**
		 * An `accept` attribute for the slot's file input (e.g. `image/jpeg,image/png`).
		 * A picker hint only — it narrows what the browse dialog offers and is NOT a
		 * validation boundary, since a drop bypasses it and the value is authored
		 * per-workflow. Omit to accept anything.
		 */
		accept: z.string().max(200).optional(),
		// A closed set, not free text: the slot's type decides what its upload
		// writes AND which deliverables its `delivery_slots_filled` guard counts.
		// An unrecognised value would be coerced at render and silently upload
		// under the wrong classification, so it is rejected at the write boundary.
		deliverableType: z.enum(DeliverableType),
	})
	.strict()

const deliverySlotsSectionOptionsSchema = z
	.object({
		slots: z.array(deliverySlotSchema).min(1).optional(),
	})
	.strict()

export type DeliverySlotConfig = z.infer<typeof deliverySlotSchema>

const sectionOptionsSchemaByCode: Record<WorkspaceSectionCode, z.ZodTypeAny> = {
	[WorkspaceSectionCode.BRIEF_SUMMARY]: emptySectionOptionsSchema,
	[WorkspaceSectionCode.GUIDELINES]: emptySectionOptionsSchema,
	[WorkspaceSectionCode.RELATED_CAMPAIGNS]: collapsibleSectionOptionsSchema,
	[WorkspaceSectionCode.RELATED_DELIVERABLES]: collapsibleSectionOptionsSchema,
	[WorkspaceSectionCode.APPLICABLE_RULES]: emptySectionOptionsSchema,
	[WorkspaceSectionCode.MESSAGES]: messagesSectionOptionsSchema,
	[WorkspaceSectionCode.DELIVERY_SLOTS]: deliverySlotsSectionOptionsSchema,
	[WorkspaceSectionCode.AI_GENERATION_CONTEXT]: emptySectionOptionsSchema,
	[WorkspaceSectionCode.FINAL_DELIVERABLES]: finalDeliverablesSectionOptionsSchema,
	[WorkspaceSectionCode.VALIDATION_RUN]: emptySectionOptionsSchema,
	[WorkspaceSectionCode.VALIDATION_PDF]: emptySectionOptionsSchema,
	[WorkspaceSectionCode.AUDIT_LOG]: emptySectionOptionsSchema,
	[WorkspaceSectionCode.DISTRIBUTION_PACKAGE]: emptySectionOptionsSchema,
}

// ─── Section entries ────────────────────────────────────────────────────────

const workspaceSectionEntrySchema = z
	.object({
		code: z.enum(WorkspaceSectionCode),
		options: z.record(z.string(), z.unknown()).optional(),
	})
	.strict()
	.superRefine((entry, ctx) => {
		if (entry.options === undefined) return
		const result = sectionOptionsSchemaByCode[entry.code].safeParse(entry.options)
		if (result.success) return
		for (const issue of result.error.issues) {
			ctx.addIssue({ ...issue, path: ['options', ...issue.path] })
		}
	})

export type WorkspaceSectionConfig = z.infer<typeof workspaceSectionEntrySchema>

// ─── Tabs ───────────────────────────────────────────────────────────────────
// The tab is the admin-authored composition unit (Atlas ToBe): a workspace
// renders a sticky tab bar and one tab's section cards at a time. Array order
// is tab order; each tab's `sections` order is card order. The Checklist tab
// is NOT authored here — it derives from the outgoing transition's pre-hooks,
// so what blocks the transition has exactly one source of truth (the
// workflow's hook configuration).

const workspaceTabKeySchema = z
	.string()
	.min(1)
	.max(32)
	.regex(/^[a-z0-9_]+$/, 'tab key must be lowercase ASCII letters, digits, or underscores')

const workspaceTabSchema = z
	.object({
		key: workspaceTabKeySchema,
		label: z.string().min(1).max(48),
		sections: z.array(workspaceSectionEntrySchema).min(1),
	})
	.strict()

export type WorkspaceTabConfig = z.infer<typeof workspaceTabSchema>

const workspaceTabListSchema = z
	.array(workspaceTabSchema)
	.min(1, 'a workspace must have at least one tab')
	.superRefine((tabs, ctx) => {
		const seen = new Set<string>()
		tabs.forEach((tab, index) => {
			if (seen.has(tab.key)) {
				ctx.addIssue({ code: 'custom', message: `duplicate tab key '${tab.key}'`, path: [index, 'key'] })
			}
			seen.add(tab.key)
		})
	})

// ─── Per-workspace-code options schemas ─────────────────────────────────────
// One shared shape — an ordered tab list — plus any workspace-specific
// top-level keys (validation keeps its `lane`). These layer on top of the
// generic JSON-object base validation the write schemas already apply.

/**
 * `z.object().strict()` alone accepts class instances with no enumerable own
 * properties (a `Date` parses as `{}`), so every per-code schema starts from
 * the same plain-object requirement the generic base validation enforces.
 */
const isPlainObject = (value: unknown): value is Record<string, unknown> => {
	if (value === null || typeof value !== 'object' || Array.isArray(value)) return false
	const proto: unknown = Object.getPrototypeOf(value)
	return proto === Object.prototype || proto === null
}

const plainObjectSchema = z.custom<Record<string, unknown>>(isPlainObject, {
	message: 'Expected a plain JSON object',
})

const tabsOnlyOptionsSchema = plainObjectSchema.pipe(
	z
		.object({
			tabs: workspaceTabListSchema.optional(),
		})
		.strict(),
)

const validationWorkspaceOptionsSchema = plainObjectSchema.pipe(
	z
		.object({
			tabs: workspaceTabListSchema.optional(),
			lane: z.enum(ValidationLane).optional(),
		})
		.strict(),
)

const workspaceOptionsSchemaByCode: Record<WorkspaceCode, z.ZodTypeAny> = {
	[WorkspaceCode.QUEUE_CARD]: tabsOnlyOptionsSchema,
	[WorkspaceCode.RESOLUTION]: tabsOnlyOptionsSchema,
	[WorkspaceCode.PRODUCTION]: tabsOnlyOptionsSchema,
	[WorkspaceCode.VALIDATION]: validationWorkspaceOptionsSchema,
	[WorkspaceCode.DISTRIBUTION]: tabsOnlyOptionsSchema,
	[WorkspaceCode.SUMMARY]: tabsOnlyOptionsSchema,
}

export interface WorkspaceOptions {
	tabs?: WorkspaceTabConfig[]
	lane?: ValidationLane
}

export const workspaceOptionsSchemaFor = (code: WorkspaceCode): z.ZodTypeAny => workspaceOptionsSchemaByCode[code]

/** Parse options for a workspace code; throws `ZodError` on mismatch (callers at the write boundary map it to a `ValidationError`). */
export const parseWorkspaceOptions = (code: WorkspaceCode, options: unknown): WorkspaceOptions =>
	workspaceOptionsSchemaByCode[code].parse(options) as WorkspaceOptions

// ─── Default composition per workspace code ─────────────────────────────────
// The Atlas ToBe composition, declared as data: when resolved options carry
// no `tabs` key, the workspace renders this list, so `{}` renders the design
// default and admins only author deviations. Defaults for workspaces that
// have not adopted tab composition yet are inert documentation until their
// component reads `tabs`.

const tab = (key: string, label: string, sections: WorkspaceSectionConfig[]): WorkspaceTabConfig => ({
	key,
	label,
	sections,
})

const section = (code: WorkspaceSectionCode, options?: Record<string, unknown>): WorkspaceSectionConfig =>
	options === undefined ? { code } : { code, options }

export const DEFAULT_WORKSPACE_TABS: Record<WorkspaceCode, readonly WorkspaceTabConfig[]> = {
	[WorkspaceCode.QUEUE_CARD]: [
		tab('brief', 'Brief', [section(WorkspaceSectionCode.BRIEF_SUMMARY)]),
		tab('guides', 'Guidelines', [section(WorkspaceSectionCode.GUIDELINES)]),
		tab('related', 'Related', [
			section(WorkspaceSectionCode.RELATED_CAMPAIGNS, { collapsed: true }),
			section(WorkspaceSectionCode.RELATED_DELIVERABLES, { collapsed: true }),
		]),
		tab('rules', 'Rules', [section(WorkspaceSectionCode.APPLICABLE_RULES)]),
		tab('messages', 'Messages', [section(WorkspaceSectionCode.MESSAGES, { editable: false })]),
	],
	[WorkspaceCode.RESOLUTION]: [
		tab('guides', 'Guidelines', [section(WorkspaceSectionCode.GUIDELINES)]),
		tab('related', 'Related', [
			section(WorkspaceSectionCode.RELATED_CAMPAIGNS, { collapsed: true }),
			section(WorkspaceSectionCode.RELATED_DELIVERABLES, { collapsed: true }),
		]),
		tab('rules', 'Rules', [section(WorkspaceSectionCode.APPLICABLE_RULES)]),
		tab('messages', 'Messages', [section(WorkspaceSectionCode.MESSAGES, { editable: false })]),
	],
	[WorkspaceCode.PRODUCTION]: [
		tab('work', 'Workspace', [
			section(WorkspaceSectionCode.DELIVERY_SLOTS),
			section(WorkspaceSectionCode.AI_GENERATION_CONTEXT),
			section(WorkspaceSectionCode.MESSAGES, { editable: true }),
		]),
		tab('activity', 'Rules & messages', [
			section(WorkspaceSectionCode.APPLICABLE_RULES),
			section(WorkspaceSectionCode.MESSAGES, { editable: false }),
		]),
		tab('rules', 'Rules', [section(WorkspaceSectionCode.APPLICABLE_RULES)]),
		tab('messages', 'Messages', [section(WorkspaceSectionCode.MESSAGES, { editable: true })]),
	],
	[WorkspaceCode.VALIDATION]: [
		tab('work', 'Workspace', [
			section(WorkspaceSectionCode.FINAL_DELIVERABLES),
			section(WorkspaceSectionCode.VALIDATION_RUN),
			section(WorkspaceSectionCode.VALIDATION_PDF),
		]),
		tab('rules', 'Rules', [section(WorkspaceSectionCode.APPLICABLE_RULES)]),
		tab('messages', 'Messages', [section(WorkspaceSectionCode.MESSAGES, { editable: false })]),
	],
	// Generating the distribution package is a hand-off step: the package download
	// that produces the artifact the `artifact_exists(distribution_zip)` guard reads.
	[WorkspaceCode.DISTRIBUTION]: [
		tab('work', 'Workspace', [section(WorkspaceSectionCode.DISTRIBUTION_PACKAGE)]),
		tab('rules', 'Rules', [section(WorkspaceSectionCode.APPLICABLE_RULES)]),
		tab('messages', 'Messages', [section(WorkspaceSectionCode.MESSAGES, { editable: false })]),
	],
	// Done is an evidence surface, not a working one — the audit log is the
	// whole default composition.
	[WorkspaceCode.SUMMARY]: [tab('work', 'Workspace', [section(WorkspaceSectionCode.AUDIT_LOG)])],
}

/**
 * The EXTERNAL validation lane's default composition (Atlas ToBe): the
 * deliverables under review on their own, without the internal lane's
 * validation run and PDF. The validation workspace picks this over
 * `DEFAULT_WORKSPACE_TABS[validation]` when its lane is compliance and no
 * tabs were authored.
 */
export const DEFAULT_EXTERNAL_VALIDATION_TABS: readonly WorkspaceTabConfig[] = [
	tab('work', 'Workspace', [section(WorkspaceSectionCode.FINAL_DELIVERABLES)]),
	tab('rules', 'Rules', [section(WorkspaceSectionCode.APPLICABLE_RULES)]),
	tab('messages', 'Messages', [section(WorkspaceSectionCode.MESSAGES, { editable: false })]),
]

/**
 * The effective ordered tab list for a workspace: the authored `options.tabs`
 * when options parse and carry one, the code's default otherwise. Safe
 * against malformed stored options by construction — reads never throw.
 */
/**
 * The design's default slots, declared as data — the visual validation file is
 * mandatory, and is what the `delivery_slots_filled(visual)` guard checks.
 *
 * The `visual` KEY is load-bearing: seeded transition hooks name the slot by it
 * (`params: { slot: 'visual' }`), and the guard resolves that name against this
 * list to find the deliverable type it must count. Renaming or dropping the key
 * silently un-gates the transition, so labels and copy are free to change but
 * the key is not.
 */
export const DEFAULT_DELIVERY_SLOTS: readonly DeliverySlotConfig[] = [
	{
		key: 'visual',
		label: 'Visual export',
		required: true,
		requirementLabel: 'MANDATORY · JPG / PNG',
		accept: 'image/jpeg,image/png',
		deliverableType: DeliverableType.FINAL_VISUAL,
	},
]

export const resolveWorkspaceTabs = (code: WorkspaceCode, options: unknown): readonly WorkspaceTabConfig[] => {
	const parsed = workspaceOptionsSchemaByCode[code].safeParse(options ?? {})
	if (!parsed.success) return DEFAULT_WORKSPACE_TABS[code]
	const tabs = (parsed.data as WorkspaceOptions).tabs
	return tabs ?? DEFAULT_WORKSPACE_TABS[code]
}
